import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BarChart3, TrendingUp, Users, FileText, AlertTriangle, DollarSign, Activity } from 'lucide-react';
import api from '../services/api';

const COLORS = ['#4caf50', '#ff9800', '#f44336', '#9e9e9e', '#2196f3', '#9c27b0'];

export default function Reportes() {
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());

  const { data: dashboard, isLoading: loadingDashboard } = useQuery({
    queryKey: ['reportes-dashboard'],
    queryFn: async () => {
      const response = await api.get('/reportes/dashboard');
      return response.data;
    },
  });

  const { data: ingresosMensuales } = useQuery({
    queryKey: ['reportes-ingresos', anioSeleccionado],
    queryFn: async () => {
      const response = await api.get(`/reportes/ingresos-mensuales?anio=${anioSeleccionado}`);
      return response.data;
    },
  });

  const { data: clientesPorMes } = useQuery({
    queryKey: ['reportes-clientes-mes', anioSeleccionado],
    queryFn: async () => {
      const response = await api.get(`/reportes/clientes-por-mes?anio=${anioSeleccionado}`);
      return response.data;
    },
  });

  const { data: facturasPorEstado } = useQuery({
    queryKey: ['reportes-facturas-estado'],
    queryFn: async () => {
      const response = await api.get('/reportes/facturas-por-estado');
      return response.data;
    },
  });

  const { data: serviciosPorPlan } = useQuery({
    queryKey: ['reportes-servicios-plan'],
    queryFn: async () => {
      const response = await api.get('/reportes/servicios-por-plan');
      return response.data;
    },
  });

  const { data: topDeudores } = useQuery({
    queryKey: ['reportes-top-deudores'],
    queryFn: async () => {
      const response = await api.get('/reportes/top-deudores');
      return response.data;
    },
  });

  if (loadingDashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <BarChart3 size={24} className="text-primary-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reportes</h1>
            <p className="text-gray-500">Estadísticas y análisis del negocio</p>
          </div>
        </div>
        <select
          value={anioSeleccionado}
          onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          {[2024, 2025, 2026].map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Clientes Activos"
          value={dashboard?.clientes?.activos || 0}
          total={dashboard?.clientes?.total || 0}
          icon={Users}
          color="blue"
        />
        <KpiCard
          title="Ingresos del Mes"
          value={`$${(dashboard?.ingresos?.mes || 0).toLocaleString('es-AR')}`}
          subtitle={`$${(dashboard?.ingresos?.anio || 0).toLocaleString('es-AR')} anual`}
          icon={DollarSign}
          color="green"
        />
        <KpiCard
          title="Facturas Pendientes"
          value={dashboard?.facturas?.pendientes || 0}
          total={dashboard?.facturas?.total || 0}
          icon={FileText}
          color="orange"
        />
        <KpiCard
          title="Servicios Activos"
          value={dashboard?.servicios?.activos || 0}
          total={dashboard?.servicios?.total || 0}
          icon={Activity}
          color="purple"
        />
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ingresos Mensuales */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <TrendingUp size={20} className="text-green-600" />
            <h3 className="text-lg font-bold text-gray-900">Ingresos Mensuales</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={ingresosMensuales}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nombre" />
              <YAxis />
              <Tooltip formatter={(value) => `$${value.toLocaleString('es-AR')}`} />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#4caf50" strokeWidth={2} name="Ingresos ($)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Clientes por Mes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Users size={20} className="text-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">Clientes Nuevos por Mes</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={clientesPorMes}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nombre" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="total" fill="#2196f3" name="Clientes Nuevos" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Facturas por Estado */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <FileText size={20} className="text-orange-600" />
            <h3 className="text-lg font-bold text-gray-900">Facturas por Estado</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={facturasPorEstado}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ estado, cantidad }) => `${estado}: ${cantidad}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="cantidad"
              >
                {facturasPorEstado?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Servicios por Plan */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Activity size={20} className="text-purple-600" />
            <h3 className="text-lg font-bold text-gray-900">Servicios por Plan</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={serviciosPorPlan}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ plan, cantidad }) => `${plan}: ${cantidad}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="cantidad"
              >
                {serviciosPorPlan?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Deudores */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex items-center space-x-2">
            <AlertTriangle size={20} className="text-red-600" />
            <h3 className="text-lg font-bold text-gray-900">Top Deudores</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Facturas Vencidas</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Deuda Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {topDeudores?.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                    No hay deudores registrados
                  </td>
                </tr>
              ) : (
                topDeudores?.map((deudor) => (
                  <tr key={deudor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{deudor.nombre}</div>
                      <div className="text-sm text-gray-500">{deudor.email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{deudor.telefono || '-'}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                        {deudor.facturasVencidas}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-red-600">
                      ${deudor.deudaTotal.toLocaleString('es-AR')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ title, value, total, subtitle, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon size={24} />
        </div>
        {total !== undefined && (
          <span className="text-sm text-gray-500">de {total}</span>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
      <p className="text-sm text-gray-500 mt-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
}
